import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { useAuth } from './use-auth'

interface TenantOption {
  id: string
  name: string
  slug: string
}

interface TenantContextValue {
  /** Currently active tenant id (overridden or from JWT) */
  activeTenantId: string
  activeTenantName: string
  /** All tenants available for switching (only for super_admin) */
  tenants: TenantOption[]
  /** Whether the user can switch tenants */
  canSwitch: boolean
  /** Switch to a different tenant — invalidates all queries */
  switchTenant: (tenantId: string) => void
}

const TenantContext = createContext<TenantContextValue | null>(null)

const STORAGE_KEY = 'roi365_active_tenant'

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isSuperAdmin = user?.role === 'super_admin'

  const [overrideId, setOverrideId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(STORAGE_KEY)
  })

  const { data: tenants } = useQuery({
    queryKey: ['admin', 'tenants-switcher'],
    queryFn: () => api.get<TenantOption[]>('/admin/tenants-switcher'),
    enabled: isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  })

  const activeTenantId = (isSuperAdmin && overrideId) ? overrideId : (user?.tenant_id ?? '')
  const activeTenantName =
    tenants?.find((t) => t.id === activeTenantId)?.name ?? user?.tenant_name ?? ''

  const switchTenant = useCallback(
    (tenantId: string) => {
      setOverrideId(tenantId)
      localStorage.setItem(STORAGE_KEY, tenantId)
      // Invalidate all non-auth queries so they refetch with new tenant
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0]
          return key !== 'auth' && key !== 'admin'
        },
      })
    },
    [queryClient],
  )

  return (
    <TenantContext.Provider
      value={{
        activeTenantId,
        activeTenantName,
        tenants: tenants ?? [],
        canSwitch: isSuperAdmin && (tenants?.length ?? 0) > 1,
        switchTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  const ctx = useContext(TenantContext)
  if (!ctx) throw new Error('useTenant must be used within TenantProvider')
  return ctx
}
